// ============================================================
// App.js
// Root of the application — routing and layout wrapper.
//
// LAYOUT:
//   - Login page renders standalone (full-screen, no sidebar)
//   - All protected pages render inside <AppLayout> which
//     provides the sidebar + header shell
//
// ROUTING (HashRouter for Electron compatibility):
//   /           → redirects to /investor-login
//   /login      → Login page (standalone)
//   /trade-center → Unified trade center + account value summary
//   /dashboard  → Robinhood Dashboard (existing, unchanged)
//   /options    → Options analytics (inside AppLayout, requires auth)
//   /trades     → Trades + stock analytics (inside AppLayout, requires auth)
//   /public          → Public.com dedicated dashboard (new)
//   /public/options  → Public.com options analytics (new)
//   /public/trades   → Public.com trade history & dividends (new)
//   /public/dividends → Public.com dividend tracking & house money (new)
//   /upload     → Upload page (inside AppLayout, requires auth)
//   /settings   → Settings page (inside AppLayout, requires auth)
// ============================================================

import { lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';

import { ThemeProvider } from './context/ThemeContext';
import { theme, fonts }   from './styles/theme';
import AppLayout           from './components/AppLayout';
import InvestorLayout      from './components/InvestorLayout';
import ProtectedRoute      from './components/ProtectedRoute';
// Upload page merged into Settings — route redirects to /settings
const Login = lazy(() => import('./pages/Login'));
const InvestorLogin = lazy(() => import('./pages/InvestorLogin'));
const ApplicationConfirmed = lazy(() => import('./pages/ApplicationConfirmed'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TradeCenter = lazy(() => import('./pages/TradeCenter'));
const Settings = lazy(() => import('./pages/Settings'));
const CryptoDashboard = lazy(() => import('./pages/CryptoDashboard'));
const Comparison = lazy(() => import('./pages/Comparison'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Risk = lazy(() => import('./pages/Risk'));
const DividendTracker = lazy(() => import('./pages/DividendTracker'));
const Phase3Dashboard = lazy(() => import('./pages/Phase3Dashboard'));
const InvestorManagement = lazy(() => import('./pages/InvestorManagement'));
const ManagerDistributions = lazy(() => import('./pages/ManagerDistributions'));
const InvestorPortal = lazy(() => import('./pages/InvestorPortal'));
const InvestorDistributions = lazy(() => import('./pages/InvestorDistributions'));
const InvestorDeposit = lazy(() => import('./pages/InvestorDeposit'));
const InvestorAccount = lazy(() => import('./pages/InvestorAccount'));
const InvestorExplainers = lazy(() => import('./pages/InvestorExplainers'));
const InvestorExplainerDetail = lazy(() => import('./pages/InvestorExplainerDetail'));
const SchwabSummary = lazy(() => import('./pages/SchwabSummary'));
const SchwabOptions = lazy(() => import('./pages/SchwabOptions'));
const SchwabTransactions = lazy(() => import('./pages/SchwabTransactions'));
const SchwabAccounts = lazy(() => import('./pages/SchwabAccounts'));
const SchwabTicker = lazy(() => import('./pages/SchwabTickerPosition'));
const FundReport = lazy(() => import('./pages/FundReport'));
const CrmContacts = lazy(() => import('./pages/CrmContacts'));
const CrmMemberProfile = lazy(() => import('./pages/CrmMemberProfile'));
const CrmPipeline = lazy(() => import('./pages/CrmPipeline'));
const CrmFirms = lazy(() => import('./pages/CrmFirms'));
const CrmRelationships = lazy(() => import('./pages/CrmRelationships'));
const CrmTasks = lazy(() => import('./pages/CrmTasks'));
const CrmActivity = lazy(() => import('./pages/CrmActivity'));
const CrmArchive = lazy(() => import('./pages/CrmArchive'));
const CrmMessages = lazy(() => import('./pages/CrmMessages'));
const CrmTemplates = lazy(() => import('./pages/CrmTemplates'));
const InvestorMessages = lazy(() => import('./pages/InvestorMessages'));

const RouteLoader = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.bg,
    color: theme.textDim,
    fontFamily: fonts.main,
    fontSize: 14,
  }}>
    <span style={{ animation: 'v2pulse 1.5s ease-in-out infinite' }}>Loading page...</span>
  </div>
);

function App() {
  return (
    <ThemeProvider>
    <HashRouter>
      <div style={{
        background:  theme.bg,
        minHeight:   '100vh',
        fontFamily:  fonts.main,
      }}>
        <Suspense fallback={<RouteLoader />}>
        <Routes>

          {/* Default: investor-facing portal domain lands on investor login */}
          <Route path="/" element={<Navigate to="/investor-login" replace />} />

          {/* Login — standalone, full-screen (no sidebar) */}
          <Route path="/login" element={<Login />} />
          <Route path="/application-confirmed" element={<ApplicationConfirmed />} />

          {/* Protected routes — wrapped in AppLayout (sidebar + header) */}

          {/* Robinhood Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Dashboard /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Schwab Account — live data from TOS/Schwab API */}
          <Route
            path="/schwab"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><SchwabAccounts /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schwab/accounts/:accountSuffix"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><SchwabSummary /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/schwab/accounts/:accountSuffix/transactions" element={<ProtectedRoute requiredRole="manager"><AppLayout><SchwabTransactions /></AppLayout></ProtectedRoute>} />
          <Route path="/schwab/accounts/:accountSuffix/options" element={<ProtectedRoute requiredRole="manager"><AppLayout><SchwabOptions /></AppLayout></ProtectedRoute>} />
          <Route path="/schwab/accounts/:accountSuffix/analytics" element={<ProtectedRoute requiredRole="manager"><Navigate to=".." relative="path" replace /></ProtectedRoute>} />
          <Route
            path="/schwab/accounts/:accountSuffix/tickers/:ticker"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><SchwabTicker /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schwab/:ticker"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><SchwabTicker /></AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/trade-center"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><TradeCenter /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/options" element={<Navigate to="/dashboard" replace />} />
          <Route path="/trades" element={<Navigate to="/dashboard" replace />} />

          <Route path="/public" element={<Navigate to="/trade-center" replace />} />
          <Route path="/public/options" element={<Navigate to="/trade-center" replace />} />
          <Route path="/public/trades" element={<Navigate to="/trade-center" replace />} />
          <Route path="/public/dividends" element={<Navigate to="/trade-center" replace />} />

          {/* Crypto Portfolio — manual source-level tracking */}
          <Route
            path="/crypto"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CryptoDashboard /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Comparison — period-over-period performance analysis */}
          <Route
            path="/comparison"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Comparison /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Calendar — daily P&L heat map */}
          <Route
            path="/calendar"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Calendar /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Risk — exposure, leverage, concentration */}
          <Route
            path="/risk"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Risk /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Dividends — passive income tracking */}
          <Route
            path="/dividends"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><DividendTracker /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Fund Report — monthly performance summary */}
          <Route
            path="/fund-report"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><FundReport /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Upload redirects to Settings */}
          <Route path="/upload" element={<Navigate to="/profile/account" replace />} />
          <Route path="/settings" element={<Navigate to="/profile/account" replace />} />

          <Route
            path="/profile/account"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Settings section="account" /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/linked"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Settings section="linked" /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/crypto"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Settings section="crypto" /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* ── FUND MANAGER ─────────────────────────────────── */}
          {/* Fund Overview — manager only */}
          <Route
            path="/phase3"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><Phase3Dashboard /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Subscriber Management — manager only */}
          <Route
            path="/subscribers"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><InvestorManagement /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/investors" element={<Navigate to="/subscribers" replace />} />

          {/* Distributions — manager only */}
          <Route
            path="/distributions"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><ManagerDistributions /></AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/crm"
            element={<Navigate to="/crm/contacts" replace />}
          />
          <Route
            path="/crm/pipeline"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmPipeline /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/firms"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmFirms /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/contacts"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmContacts /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/contacts/:contactId"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmMemberProfile /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/relationships"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmRelationships /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/tasks"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmTasks /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/activity"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmActivity /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/messages"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmMessages /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/templates"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmTemplates /></AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/archive"
            element={
              <ProtectedRoute requiredRole="manager">
                <AppLayout><CrmArchive /></AppLayout>
              </ProtectedRoute>
            }
          />

          {/* ── INVESTOR PORTAL ─────────────────────────────── */}
          {/* Separate login page for investors (no register) */}
          <Route path="/investor-login" element={<InvestorLogin />} />

          {/* Investor portal pages — investor role only */}
          <Route
            path="/portal"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorPortal /></InvestorLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/distributions"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorDistributions /></InvestorLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/deposit"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorDeposit /></InvestorLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/messages"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorMessages /></InvestorLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/account"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorAccount /></InvestorLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/explainers"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorExplainers /></InvestorLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/explainers/:slug"
            element={
              <ProtectedRoute requiredRole="investor">
                <InvestorLayout><InvestorExplainerDetail /></InvestorLayout>
              </ProtectedRoute>
            }
          />

        </Routes>
        </Suspense>
      </div>
    </HashRouter>
    </ThemeProvider>
  );
}

export default App;
